import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Drawer,
  Card,
  CardContent,
  Button,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Popover,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PaletteIcon from "@mui/icons-material/Palette";

import { ChromePicker } from "react-color";
import TableDiff from "./VehicleBrandTableDiff";
import { useTableActions } from "../Recruit/RecJobs/SubComponents/hooks/useTableActions";

import { VehicleModelsApis, vehicleBrandsApi } from "../../Data/axios";
import { getUserInfoProperty } from "../../Data/UserData";
import config from "../../Data/AuthUser";
import StatusAlert from "../../StatusAlertList/StatusAlert";
import { useSelector } from "react-redux";
function VehicleModel() {
  const userPermissions = useSelector((state) => state?.user?.permissions);

  // Drawer and form states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [model, setModel] = useState("");
  const [wheels, setWheels] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [color, setColor] = useState("#ff0000");

  // Data states
  const [vehicleModels, setVehicleModels] = useState([]);
  const [vehicleBrands, setVehicleBrands] = useState([]);

  // Image and file states
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  // Alert states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [loading, setLoading] = useState(false);

  // Color picker states
  const [anchorEl, setAnchorEl] = useState(null);

  // Pagination states
  const [queries, setQueries] = useState({
    page: 1,
    per_page: 10,
  });

  const showAlert = useCallback((message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  }, []);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    // Reset form data when closing
    setSelectedBrandId("");
    setModel("");
    setWheels("");
    setFuelType("");
    setColor("#ff0000");
    setSelectedFile(null);
    setImageUrl(null);
  };
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setImageUrl(null);
      showAlert("Please select an image file.", "warning");
    }
  };

  // Color picker handlers
  const handleColorClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setAnchorEl(null);
  };

  const handleColorChange = (color) => {
    setColor(color.hex);
  };

  // Fetch vehicle models data
  const fetchVehicleModels = useCallback(async () => {
    try {
      setLoading(true);

      const response = await VehicleModelsApis.getAll();

      if (Array.isArray(response.data)) {
        // Format data for TableDiff
        const processedData = response.data.map((item) => ({
          id: item.id || 0,
          model: item.model || "",
          wheels: item.wheels || "",
          fuel: item.fuel || "Diesel",
          brand_id: item.brand_id || "",
          color: item.color || "#000000",
          engine: item.engine || "",
          // Add engine display for rendering
          engineDisplay: item.engine ? (
            <img
              src={config.apiImageUrl + item.engine}
              alt="Engine"
              style={{
                width: 50,
                height: 50,
                borderRadius: "8px",
                objectFit: "cover",
              }}
            />
          ) : (
            "No Image"
          ),
        }));

        setVehicleModels(processedData);
      } else {
        console.error("API response is not an array:", response.data);
        setVehicleModels([]);
      }
    } catch (error) {
      console.error("Error fetching vehicle models:", error);
      setVehicleModels([]);
      showAlert("Error fetching vehicle models", "error");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  // Fetch vehicle brands for dropdown
  const fetchVehicleBrands = useCallback(async () => {
    try {
      const response = await vehicleBrandsApi.getAll();
      if (Array.isArray(response?.data)) {
        setVehicleBrands(response.data);
      } else if (Array.isArray(response)) {
        setVehicleBrands(response);
      } else {
        setVehicleBrands([]);
      }
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      setVehicleBrands([]);
    }
  }, []);
  // Save vehicle model
  const saveVehicleModel = async (event) => {
    event.preventDefault();

    if (
      !selectedBrandId ||
      !model.trim() ||
      !wheels.trim() ||
      !fuelType ||
      !selectedFile
    ) {
      showAlert(
        "Please fill all fields and select an engine image.",
        "warning"
      );
      return;
    }

    setLoading(true);
    const last_update = new Date().getTime();

    const formData = new FormData();
    formData.append("vehicle_brand_id", selectedBrandId);
    formData.append("model", model.trim());
    formData.append("wheels", wheels.trim());
    formData.append("fuel", fuelType);
    formData.append("color", color);
    formData.append("engine", selectedFile);
    formData.append("createby", getUserInfoProperty("empid"));
    formData.append("last_update", last_update.toString());

    try {
      const response = await VehicleModelsApis.add(formData);

      // if (response === "1602" || response.status === "success") {
      //   showAlert("Vehicle model added successfully", "success");
      fetchVehicleModels();
      handleDrawerClose();
      // } else if (response === "1601") {
      //   showAlert("Vehicle model already exists", "warning");
      // } else {
      //   showAlert("Unexpected response from server", "warning");
      // }
    } catch (error) {
      console.error("Error adding vehicle model:", error);
      showAlert("Error adding vehicle model.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Use table actions hook
  const defaultTableActions = useTableActions(
    VehicleModelsApis,
    fetchVehicleModels
  );

  // Override handleActionClick to customize behavior
  const tableActions = {
    ...defaultTableActions,
    handleActionClick: async (action, row) => {
      if (action === "Delete") {
        if (window.confirm(`Are you sure you want to delete ${row.model}?`)) {
          try {
            await VehicleModelsApis.deletebyId(row.id);
            showAlert("Vehicle model deleted successfully", "success");
            fetchVehicleModels();
          } catch (error) {
            console.error("Error deleting vehicle model:", error);
            showAlert("Error deleting vehicle model", "error");
          }
        }
      }
    },
  };

  useEffect(() => {
    fetchVehicleModels();
    fetchVehicleBrands();
  }, [fetchVehicleModels, fetchVehicleBrands]);

  // Table headers configuration
  const headers = [
    {
      key: "engineDisplay",
      label: "Engine",
      sortable: false,
    },
    { key: "model", label: "Model", sortable: true },
    { key: "wheels", label: "Wheels", sortable: true },
    { key: "fuel", label: "Fuel Type", sortable: true },
  ];

  const handlePageChange = (newPage) => {
    setQueries({ ...queries, page: newPage });
  };

  const handlePerPageChange = (newPerPage) => {
    setQueries({ ...queries, per_page: newPerPage, page: 1 });
  };

  const openColor = Boolean(anchorEl);

  return (
    <div>
      <StatusAlert
        open={alertOpen}
        setOpen={setAlertOpen}
        message={alertMessage}
        severity={alertSeverity}
      />

      {/* Add Vehicle Model Button */}
      <Box
        display="flex"
        justifyContent="flex-start"
        sx={{ marginBottom: "16px" }}
      >
        {userPermissions?.vehical_data_model_add !== 0 && (
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={handleDrawerOpen}
            sx={{
              bgcolor: "#4285F4",
              color: "white",
              "&:hover": {
                bgcolor: "#3367D6",
              },
              marginTop: "10px",
              marginLeft: "16px",
              paddingY: "8px",
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            Add Vehicle Model
          </Button>
        )}
      </Box>

      {/* TableDiff Component */}
      <TableDiff
        headers={headers}
        data={vehicleModels}
        rowKey="id"
        defaultSortKey=""
        tableActions={tableActions}
        tableApis={VehicleModelsApis}
        onTableUpdate={fetchVehicleModels}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        queries={queries}
        type="vehicle-models"
        tableClassName="vehicle-models-table"
        tableStyle={{ fontSize: "16px", fontFamily: "Ubuntu" }}
      />

      {/* Add Vehicle Model Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "75%", md: "75%" },
            backgroundColor: "#ffffff",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#000000",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{ color: "#000000", fontWeight: 600 }}
            >
              Add Vehicle Model
            </Typography>
            <IconButton
              edge="end"
              onClick={handleDrawerClose}
              aria-label="close"
              sx={{ color: "#424242" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Form Content */}
          <Box
            sx={{
              flexGrow: 1,
              p: 3,
              overflow: "auto",
              backgroundColor: "#fafafa",
            }}
          >
            {/* Vehicle Brand Selection */}
            <Card elevation={1} sx={{ mb: 3, backgroundColor: "#ffffff" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "#1976d2", fontWeight: 600 }}
                >
                  Vehicle Information
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#333", fontWeight: 500 }}
                  >
                    Vehicle Brand *
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={selectedBrandId}
                      onChange={(e) => setSelectedBrandId(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: "#ffffff",
                        "& .MuiSelect-select": {
                          color: "#000000",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                      }}
                    >
                      <MenuItem disabled value="">
                        <em>Select Vehicle Brand</em>
                      </MenuItem>
                      {vehicleBrands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#333", fontWeight: 500 }}
                  >
                    Model Name *
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="e.g. Truck, Bus, Tempo"
                    type="text"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "& input": {
                          color: "#000000",
                        },
                        "&:hover fieldset": {
                          borderColor: "#1976d2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#333", fontWeight: 500 }}
                  >
                    Number of Wheels *
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="e.g. 4, 6, 10"
                    type="text"
                    value={wheels}
                    onChange={(e) => setWheels(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "& input": {
                          color: "#000000",
                        },
                        "&:hover fieldset": {
                          borderColor: "#1976d2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#333", fontWeight: 500 }}
                  >
                    Fuel Type *
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={fuelType}
                      onChange={(e) => setFuelType(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: "#ffffff",
                        "& .MuiSelect-select": {
                          color: "#000000",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                      }}
                    >
                      <MenuItem disabled value="">
                        <em>Select Fuel Type</em>
                      </MenuItem>
                      <MenuItem value="CNG">CNG</MenuItem>
                      <MenuItem value="Petrol">Petrol</MenuItem>
                      <MenuItem value="Diesel">Diesel</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#333", fontWeight: 500 }}
                  >
                    Vehicle Color
                  </Typography>
                  <TextField
                    type="text"
                    value={color}
                    onClick={handleColorClick}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <IconButton>
                          <PaletteIcon />
                        </IconButton>
                      ),
                      style: {
                        backgroundColor: color,
                        color: color === "#ffffff" ? "#000000" : "#ffffff",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#1976d2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                  />
                  <Popover
                    open={openColor}
                    anchorEl={anchorEl}
                    onClose={handleColorClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                  >
                    <ChromePicker
                      color={color}
                      onChangeComplete={handleColorChange}
                    />
                  </Popover>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#333", fontWeight: 500 }}
                  >
                    Engine Image *
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      height: "56px",
                      borderStyle: "dashed",
                      borderColor: "#1976d2",
                      backgroundColor: "#ffffff",
                      color: "#424242",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        borderColor: "#1976d2",
                      },
                    }}
                  >
                    {selectedFile ? selectedFile.name : "Choose Engine Image"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </Button>

                  {imageUrl && (
                    <Card
                      elevation={1}
                      sx={{
                        mt: 2,
                        textAlign: "center",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ color: "#424242" }}
                        >
                          Preview:
                        </Typography>
                        <img
                          src={imageUrl}
                          alt="Selected engine"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "200px",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                          }}
                        />
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "#ffffff",
              display: "flex",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={saveVehicleModel}
              disabled={
                loading ||
                !selectedBrandId ||
                !model.trim() ||
                !wheels.trim() ||
                !fuelType ||
                !selectedFile
              }
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: "#1976d2",
                color: "#ffffff",
                "&:hover": { backgroundColor: "#1565c0" },
                "&:disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              {loading ? "Saving..." : "Save Model"}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleDrawerClose}
              disabled={loading}
              sx={{
                borderColor: "#424242",
                color: "#424242",
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
}

export default VehicleModel;
